export const ROLES = ['CUSTOMER', 'STAFF', 'KITCHEN', 'DRIVER', 'MANAGER', 'OWNER', 'PLATFORM_ADMIN'] as const;
export type Role = (typeof ROLES)[number];

/** Roles a restaurant can give to its employees (CUSTOMER and PLATFORM_ADMIN are not employee roles). */
export const EMPLOYEE_ROLES = ['STAFF', 'KITCHEN', 'DRIVER', 'MANAGER', 'OWNER'] as const;
export type EmployeeRole = (typeof EMPLOYEE_ROLES)[number];

export const PERMISSIONS = [
  'dashboard.view',
  'orders.view',
  'orders.update_status',
  'orders.cancel',
  'orders.qr',
  'kitchen.view',
  'dispatch.view',
  'dispatch.assign',
  'simulator.run',
  'menu.manage',
  'customers.view',
  'employees.view',
  'employees.manage',
  'finance.view',
  'settings.manage',
  'driver.deliver',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL_RESTAURANT: Permission[] = PERMISSIONS.filter((p) => p !== 'driver.deliver');

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  CUSTOMER: [],
  KITCHEN: ['orders.view', 'orders.update_status', 'orders.qr', 'kitchen.view'],
  STAFF: [
    'dashboard.view',
    'orders.view',
    'orders.update_status',
    'orders.qr',
    'kitchen.view',
    'dispatch.view',
    'dispatch.assign',
    'customers.view',
  ],
  DRIVER: ['driver.deliver'],
  MANAGER: ALL_RESTAURANT,
  OWNER: ALL_RESTAURANT,
  PLATFORM_ADMIN: [],
};

export function permissionsFor(roles: readonly Role[]): Permission[] {
  const set = new Set<Permission>();
  for (const r of roles) for (const p of ROLE_PERMISSIONS[r] ?? []) set.add(p);
  return [...set];
}

export function hasPermission(roles: readonly Role[], perm: Permission): boolean {
  return roles.some((r) => (ROLE_PERMISSIONS[r] ?? []).includes(perm));
}

/** Who may give which role. Only an OWNER can create or demote another OWNER. */
export function canAssignRole(actorRoles: readonly Role[], target: EmployeeRole): boolean {
  if (actorRoles.includes('OWNER')) return true;
  if (actorRoles.includes('MANAGER')) return target !== 'OWNER';
  return false;
}

export const ROLE_LABELS_FR: Record<Role, string> = {
  CUSTOMER: 'Client',
  STAFF: 'Comptoir',
  KITCHEN: 'Cuisine',
  DRIVER: 'Livreur',
  MANAGER: 'Gérant',
  OWNER: 'Propriétaire',
  PLATFORM_ADMIN: 'Admin plateforme',
};

export const ROLE_LABELS_EN: Record<Role, string> = {
  CUSTOMER: 'Customer',
  STAFF: 'Front counter',
  KITCHEN: 'Kitchen',
  DRIVER: 'Driver',
  MANAGER: 'Manager',
  OWNER: 'Owner',
  PLATFORM_ADMIN: 'Platform admin',
};

export const ROLE_LABELS = { en: ROLE_LABELS_EN, fr: ROLE_LABELS_FR } as const;
